import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  deleteDoc,
  query,
  where
} from 'firebase/firestore';
import { db } from '../../firebase';
import { formatMessageTime, getOtherParticipant } from '../../utils/messaging';

const ConversationManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [users, setUsers] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    } else {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    applyFilters();
  }, [conversations, filters]);

  const checkAdminStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin) {
        setIsAdmin(true);
        fetchData();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/');
    }
  };

  const fetchData = async () => {
    try {
      // Fetch conversations and users in parallel
      const [conversationsSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, 'conversations')),
        getDocs(collection(db, 'users'))
      ]);

      // Build users lookup
      const usersData = {};
      usersSnapshot.forEach((doc) => {
        usersData[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Build conversations data
      const conversationsData = [];
      conversationsSnapshot.forEach((doc) => {
        const conversationData = { id: doc.id, ...doc.data() };
        
        // Add participant names
        conversationData.participantNames = conversationData.participants?.map(userId => {
          const user = usersData[userId];
          return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
        }).join(', ') || 'Unknown Participants';

        conversationsData.push(conversationData);
      });

      // Sort by last activity (newest first)
      conversationsData.sort((a, b) => {
        const aTime = a.lastActivity?.toDate?.() || new Date(a.lastActivity) || new Date(0);
        const bTime = b.lastActivity?.toDate?.() || new Date(b.lastActivity) || new Date(0);
        return bTime - aTime;
      });

      setUsers(usersData);
      setConversations(conversationsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...conversations];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(conversation => 
        conversation.participantNames?.toLowerCase().includes(searchTerm) ||
        conversation.lastMessage?.content?.toLowerCase().includes(searchTerm) ||
        conversation.id.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status) {
      if (filters.status === 'active') {
        filtered = filtered.filter(conversation => conversation.isActive !== false);
      } else if (filters.status === 'inactive') {
        filtered = filtered.filter(conversation => conversation.isActive === false);
      }
    }

    setFilteredConversations(filtered);
  };

  const handleViewMessages = async (conversation) => {
    try {
      setSelectedConversation(conversation);
      
      // Fetch messages for this conversation
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversation.id)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData = [];
      
      messagesSnapshot.forEach((doc) => {
        messagesData.push({ id: doc.id, ...doc.data() });
      });

      // Sort by timestamp
      messagesData.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp) || new Date(0);
        const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp) || new Date(0);
        return aTime - bTime;
      });

      setConversationMessages(messagesData);
      setShowMessagesModal(true);
    } catch (error) {
      console.error('Error fetching messages:', error);
      alert('Error fetching messages: ' + error.message);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This will also delete all associated messages.')) {
      return;
    }

    try {
      // Delete all messages in this conversation first
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const deletePromises = [];
      
      messagesSnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      // Delete all messages
      await Promise.all(deletePromises);
      
      // Delete the conversation
      await deleteDoc(doc(db, 'conversations', conversationId));
      
      // Update local state
      setConversations(conversations.filter(conv => conv.id !== conversationId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Error deleting conversation: ' + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <div className="loading">Loading conversations...</div>;
  }

  if (!isAdmin) {
    return <div className="error">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="admin-conversation-management">
      <div className="admin-header">
        <div className="admin-breadcrumb">
          <button onClick={() => navigate('/admin/dashboard')} className="breadcrumb-link">
            ← Back to Admin Dashboard
          </button>
        </div>
        <h2>Conversation Management</h2>
        <p>View and manage user conversations</p>
      </div>

      <div className="conversation-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search conversations..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="conversation-search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="conversation-filter-select"
          >
            <option value="">All Conversations</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="admin-content">
        <div className="conversation-stats">
          <div className="conversation-stat-item">
            <span className="conversation-stat-label">Total Conversations:</span>
            <span className="conversation-stat-value">{conversations.length}</span>
          </div>
          <div className="conversation-stat-item">
            <span className="conversation-stat-label">Filtered:</span>
            <span className="conversation-stat-value">{filteredConversations.length}</span>
          </div>
        </div>

        <div className="conversations-grid">
          {filteredConversations.map(conversation => (
            <div key={conversation.id} className="conversation-card">
              <div className="conversation-card-header">
                <div className="conversation-id-badge">
                  {conversation.id.substring(0, 8)}...
                </div>
                <div className="conversation-status">
                  <span className={`conversation-status-badge ${conversation.isActive !== false ? 'active' : 'inactive'}`}>
                    {conversation.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div className="conversation-card-content">
                <div className="participants-section">
                  <div className="participants-title">Participants</div>
                  <div className="participants-list">
                    {conversation.participantNames}
                  </div>
                  <div className="participant-count">
                    {conversation.participants?.length || 0} participants
                  </div>
                </div>
                
                <div className="last-message-section">
                  <div className="message-title">Last Message</div>
                  <div className="message-content">
                    {conversation.lastMessage?.content || 'No messages'}
                  </div>
                  {conversation.lastMessage?.senderName && (
                    <div className="message-sender">
                      by {conversation.lastMessage.senderName}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="conversation-card-footer">
                <div className="last-activity">
                  <div className="activity-label">Last Activity</div>
                  <div className="activity-time">
                    {formatDate(conversation.lastActivity)}
                  </div>
                </div>
                <div className="conversation-actions">
                  <button 
                    className="conversation-view-btn"
                    onClick={() => handleViewMessages(conversation)}
                  >
                    View Messages
                  </button>
                  <button 
                    className="conversation-delete-btn"
                    onClick={() => handleDeleteConversation(conversation.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Messages Modal */}
      {showMessagesModal && selectedConversation && (
        <div className="conversation-modal-overlay">
          <div className="conversation-modal-content">
            <div className="conversation-modal-header">
              <h3>Messages: {selectedConversation.participantNames}</h3>
              <button 
                className="conversation-modal-close-btn"
                onClick={() => setShowMessagesModal(false)}
              >
                ×
              </button>
            </div>

            <div className="conversation-modal-body">
              <div className="conversation-info-section">
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-label">Conversation ID</div>
                    <div className="info-value">{selectedConversation.id}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Created</div>
                    <div className="info-value">{formatDate(selectedConversation.createdAt)}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Last Activity</div>
                    <div className="info-value">{formatDate(selectedConversation.lastActivity)}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Total Messages</div>
                    <div className="info-value">{conversationMessages.length}</div>
                  </div>
                </div>
              </div>

              <div className="conversation-messages-section">
                <div className="messages-header">
                  <h4>Message History</h4>
                </div>
                <div className="messages-list">
                  {conversationMessages.length === 0 ? (
                    <div className="no-messages">
                      <div className="no-messages-icon">💬</div>
                      <p>No messages in this conversation</p>
                    </div>
                  ) : (
                    conversationMessages.map(message => (
                      <div key={message.id} className="conversation-message-item">
                        <div className="message-avatar">
                          {message.senderName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="message-content-wrapper">
                          <div className="message-header">
                            <span className="message-sender-name">{message.senderName}</span>
                            <span className="message-timestamp">
                              {formatDate(message.timestamp)}
                            </span>
                          </div>
                          <div className="message-text">
                            {message.content}
                          </div>
                          <div className="message-meta-info">
                            <span className="message-type-badge">{message.messageType}</span>
                            <span className={`message-read-status ${message.isRead ? 'read' : 'unread'}`}>
                              {message.isRead ? '✓ Read' : '○ Unread'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="conversation-modal-actions">
              <button 
                className="conversation-modal-close-btn-footer"
                onClick={() => setShowMessagesModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationManagement;